import { Permission, notEqual } from '@cims-online/shared'
import { EditRowActionOptions } from 'components/CIMSTable/CIMSTableContext/types/actions/row'
import { PayItemConnectionGroup, useGroupsContext } from '../../GroupsContext'
import { CreateEditBodyValues } from '../../components/CreateEditBody/types'
import CreateEditBody from '../../components/CreateEditBody'
import { useEditPayItemConnectionGroupMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleEditNotification } from 'hooks/notificationHandler'

export function useEdit(): EditRowActionOptions<
  PayItemConnectionGroup,
  CreateEditBodyValues
> {
  const { toConnectionValues, fromConnectionValues, refetch } =
    useGroupsContext()
  const [edit] = useEditPayItemConnectionGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editPayItemConnectionGroup: {
        payItemConnectionGroup: { name },
      },
    }) => simpleEditNotification(name),
  })
  return {
    permission: Permission.EditPayItemConnectionGroup,
    modal: {
      type: 'simple',
      recordName: 'name',
      body: <CreateEditBody />,
      initialValues: ({
        name,
        description,
        connections,
        internalConnections,
        workflowConnections,
      }) => ({
        name,
        description,
        payItemConnections: toConnectionValues({
          connections,
          internalIds: internalConnections.map(
            ({ internalConnectionId }) => internalConnectionId
          ),
          workflowConnections,
        }),
      }),
      onOk: async (
        {
          id,
          connections,
          name: currentName,
          description: currentDescription,
          internalConnections,
          workflowConnections,
        },
        { name, payItemConnections, description }
      ) => {
        const { connectionIds, internalConnectionIds, workflowIds } =
          fromConnectionValues(payItemConnections)
        const connectionMembershipChanged = notEqual(
          connections.map(({ id }) => id),
          connectionIds
        )
        const internalMembershipChanged = notEqual(
          internalConnections.map(
            ({ internalConnectionId }) => internalConnectionId
          ),
          internalConnectionIds
        )
        const workflowMembershipChanged = notEqual(
          workflowConnections.map(({ workflowId }) => workflowId),
          workflowIds
        )
        if (
          connectionMembershipChanged ||
          currentName !== name ||
          currentDescription !== description ||
          internalMembershipChanged ||
          workflowMembershipChanged
        ) {
          await edit({
            variables: {
              input: {
                id,
                name,
                description,
                ...(connectionMembershipChanged ? { connectionIds } : {}),
                ...(internalMembershipChanged ? { internalConnectionIds } : {}),
                ...(workflowMembershipChanged ? { workflowIds } : {}),
              },
            },
          })
          refetch()
        }
      },
    },
  }
}
